import { CihSectionHeader } from "~/components/cih/section-header";
import { VerifiedBadge } from "~/components/coach-profile/verified-badge";
import {
  verifiedByText,
  type CredentialVerification,
} from "~/lib/shared/cco-claims";
import { toExternalHref } from "~/lib/shared/link-rel";

type Tile = {
  key: string;
  href?: string;
  title: string;
  /** Optional context line below the title — e.g. an issuing body for CTPs. */
  issuer?: string | null;
  logoUrl: string | null;
  verification: CredentialVerification | null;
  /** Used for the "external link" affordance + target=_blank on manual cert URLs. */
  external?: boolean;
};

export type CoachCredentialsListProps = {
  ccoMemberships: {
    ccoId: string;
    ccoName: string;
    ccoSlug: string;
    ccoLogoUrl: string | null;
    isPublished: boolean;
    verification: CredentialVerification | null;
  }[];
  ctpCertifications: {
    ctpId: string;
    ctpName: string;
    ctpSlug: string;
    ctpLogoUrl: string | null;
    ccoName: string;
    ccoSlug: string;
    ccoLogoUrl: string | null;
    certificationDate: Date | null;
    isPublished: boolean;
    verification: CredentialVerification | null;
  }[];
  certifications: {
    id: string;
    title: string;
    imageSrc: string | null;
    url: string | null;
    verified: boolean;
  }[];
};

/** Credential tiles grid — no section wrapper or header. Self-nulls when there are zero tiles. */
export function CoachCredentialsList({
  ccoMemberships,
  ctpCertifications,
  certifications,
}: CoachCredentialsListProps) {
  const tiles: Tile[] = [
    ...ccoMemberships.map((m) => ({
      key: `cco-${m.ccoId}`,
      href: m.isPublished ? `/organizations/${m.ccoSlug}` : undefined,
      title: m.ccoName,
      issuer: "Member organization",
      logoUrl: m.ccoLogoUrl,
      verification: m.verification,
    })),
    ...ctpCertifications.map((c) => ({
      key: `ctp-${c.ctpId}`,
      href: c.isPublished ? `/training-programs/${c.ctpSlug}` : undefined,
      title: c.ctpName,
      issuer: c.ccoName ? `Certified by ${c.ccoName}` : null,
      logoUrl: c.ctpLogoUrl ?? c.ccoLogoUrl ?? null,
      verification: c.verification,
    })),
    ...certifications.map((cert) => ({
      key: `cert-${cert.id}`,
      // Coach-entered free text: `caitlindianna.com` without a scheme would
      // render as a relative anchor the browser resolves against the profile,
      // producing an internal 404 (`/caitlindianna.com`) instead of an
      // outbound link. `toExternalHref` is idempotent, so values already
      // stored as full URLs pass through untouched.
      href: toExternalHref(cert.url) || undefined,
      title: cert.title,
      issuer: null,
      logoUrl: cert.imageSrc,
      // Only DCM staff mark a coach-entered certification verified.
      verification: cert.verified
        ? { decider: { kind: "dcm" as const }, date: null }
        : null,
      external: true,
    })),
  ];

  if (tiles.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {tiles.map((tile) => (
        <CredentialTile key={tile.key} tile={tile} />
      ))}
    </div>
  );
}

type StripProps = CoachCredentialsListProps & { sectionNumber: number };

export function CoachCredentialsStrip({
  ccoMemberships,
  ctpCertifications,
  certifications,
  sectionNumber,
}: StripProps) {
  const hasContent =
    ccoMemberships.length > 0 ||
    ctpCertifications.length > 0 ||
    certifications.length > 0;
  if (!hasContent) return null;

  return (
    <section id="credentials" className="border-t border-gray-200 bg-purple-50">
      <CihSectionHeader
        label={`${String(sectionNumber).padStart(2, "0")} · Credentials`}
        title="Training & memberships"
      />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <CoachCredentialsList
          ccoMemberships={ccoMemberships}
          ctpCertifications={ctpCertifications}
          certifications={certifications}
        />
      </div>
    </section>
  );
}

function CredentialTile({ tile }: { tile: Tile }) {
  const inner = (
    <div className="flex h-full items-center gap-4">
      <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white">
        {tile.logoUrl ? (
          <img
            src={tile.logoUrl}
            alt=""
            className="max-h-10 max-w-10 object-contain"
          />
        ) : (
          <span aria-hidden className="font-heading text-base text-gray-300">
            {tile.title.slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="leading-tight font-medium text-gray-900">
          {tile.title}
        </div>
        {tile.issuer && (
          <div className="mt-0.5 text-xs text-gray-500">{tile.issuer}</div>
        )}
        {tile.verification && (
          <VerifiedBadge
            decider={tile.verification.decider}
            date={tile.verification.date}
          />
        )}
      </div>
    </div>
  );

  const className =
    "rounded-xl border border-gray-200 bg-white p-4 transition-colors";

  if (tile.href) {
    const ariaLabelParts = [
      tile.title,
      tile.issuer ?? null,
      tile.verification
        ? verifiedByText(tile.verification.decider, tile.verification.date)
        : null,
      tile.external ? "(opens in new tab)" : null,
    ].filter((s): s is string => Boolean(s));
    return (
      <a
        href={tile.href}
        target={tile.external ? "_blank" : undefined}
        rel={tile.external ? "noreferrer" : undefined}
        aria-label={ariaLabelParts.join(" · ")}
        className={`${className} hover:border-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600`}
      >
        {inner}
      </a>
    );
  }
  return <div className={className}>{inner}</div>;
}
